import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  PRODUCT_FORM_FIELD_CLASS,
  PRODUCT_FORM_INLINE_BUTTON_CLASS,
  PRODUCT_FORM_LABEL_CLASS,
  PRODUCT_FORM_REQUIRED_CLASS,
  PRODUCT_FORM_ROW_CLASS,
  PRODUCT_FORM_TEXTAREA_CLASS,
} from '@/components/commerce-v2/productFormChrome';
import { ImageDropZone } from '@/components/ui/image-drop-zone';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, X } from 'lucide-react';
import { woocommerceApi } from '@/lib/api/services/woocommerce';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { validateProductImageUrl } from '@/lib/commerce/productImageUrl';
import {
  buildWooProductPayload,
  EMPTY_WOO_PRODUCT_FORM,
  type WooImageItem,
  type WooProductFormValue,
} from '@/lib/commerce/storeProductPayload';
import {
  WOO_PRODUCT_STATUSES,
  WOO_STOCK_STATUSES,
  type WooProductStatus,
  type WooStockStatus,
} from '@/lib/commerce/productVocabulary';


export {
  EMPTY_WOO_PRODUCT_FORM,
  buildWooProductPayload,
} from '@/lib/commerce/storeProductPayload';
export type {
  WooImageItem,
  WooProductFormValue,
} from '@/lib/commerce/storeProductPayload';

const WOO_STATUS_LABELS: Record<WooProductStatus, string> = {
  publish: 'Published',
  draft: 'Draft',
  pending: 'Pending',
  private: 'Private',
};

const WOO_STOCK_STATUS_LABELS: Record<WooStockStatus, string> = {
  instock: 'In Stock',
  outofstock: 'Out of Stock',
  onbackorder: 'On Backorder',
};

export const WOO_STATUS_OPTIONS = WOO_PRODUCT_STATUSES.map((value) => ({
  value,
  label: WOO_STATUS_LABELS[value],
}));

export const WOO_STOCK_STATUS_OPTIONS = WOO_STOCK_STATUSES.map((value) => ({
  value,
  label: WOO_STOCK_STATUS_LABELS[value],
}));

export interface WooProductForm {
  value: WooProductFormValue;
  set: (key: keyof WooProductFormValue, val: string) => void;
  images: WooImageItem[];
  setImages: React.Dispatch<React.SetStateAction<WooImageItem[]>>;
  imageUrl: string;
  setImageUrl: (value: string) => void;
  addImageUrl: () => void;
  businessId: string;
  currency: string;
  loading: boolean;
  uploading: boolean;
  canSubmit: boolean;
  reset: () => void;
  submit: () => Promise<{ ok: boolean; productId?: string }>;
}

export function useWooProductForm({
  open,
  businessId,
  currency = 'USD',
}: {
  open: boolean;
  businessId: string;
  currency?: string;
}): WooProductForm {
  const [value, setValue] = React.useState<WooProductFormValue>({
    ...EMPTY_WOO_PRODUCT_FORM,
  });
  const [images, setImages] = React.useState<WooImageItem[]>([]);
  const [imageUrl, setImageUrl] = React.useState('');

  const addImageUrl = React.useCallback(() => {
    const raw = imageUrl.trim();
    const problem = validateProductImageUrl(raw);
    if (problem) {
      toast({ title: problem, variant: 'warning' });
      return;
    }
    if (images.some((img) => img.url === raw)) {
      toast({ title: 'That image is already added', variant: 'warning' });
      setImageUrl('');
      return;
    }
    setImages((prev) => [...prev, { url: raw }]);
    setImageUrl('');
  }, [imageUrl, images]);
  const [loading, setLoading] = React.useState(false);

  const set = (key: keyof WooProductFormValue, val: string) =>
    setValue((prev) => ({ ...prev, [key]: val }));

  const reset = React.useCallback(() => {
    setValue({ ...EMPTY_WOO_PRODUCT_FORM });
    setImages([]);
    setImageUrl('');
  }, []);

  const uploading = images.some((img) => img.uploading);

  const submit = async (): Promise<{ ok: boolean; productId?: string }> => {
    if (!businessId) return { ok: false };
    if (!value.name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a product name.',
        variant: 'warning',
      });
      return { ok: false };
    }
    if (uploading) {
      toast({
        title: 'Please wait',
        description: 'Images are still uploading.',
        variant: 'warning',
      });
      return { ok: false };
    }

    setLoading(true);
    try {
      const created = await woocommerceApi.createProduct(businessId, {
        type: 'simple',
        ...buildWooProductPayload(value, images),
      });
      toast({
        variant: 'success',
        title: 'Product Created',
        description: `"${value.name}" was added to your store.`,
      });
      const createdId =
        created?.product?.id != null ? String(created.product.id) : undefined;
      reset();
      return { ok: true, productId: createdId };
    } catch (e) {
      showApiError(e, { fallback: { title: 'Create Failed' } });
      return { ok: false };
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!open) return;
    reset();
  }, [open, reset]);

  return {
    value,
    set,
    images,
    setImages,
    imageUrl,
    setImageUrl,
    addImageUrl,
    businessId,
    currency,
    loading,
    uploading,
    canSubmit: Boolean(value.name.trim()) && !uploading,
    reset,
    submit,
  };
}

export function WooProductFormFields({
  form,
  currency,
  onChange,
  images,
  onImagesChange,
  imageUrl,
  onImageUrlChange,
  onAddImageUrl,
  businessId,
}: {
  form: WooProductFormValue;
  currency: string;
  onChange: (key: keyof WooProductFormValue, val: string) => void;
  images: WooImageItem[];
  onImagesChange: React.Dispatch<React.SetStateAction<WooImageItem[]>>;
  imageUrl?: string;
  onImageUrlChange?: (value: string) => void;
  onAddImageUrl?: () => void;
  businessId: string;
}) {
  const fileInputId = React.useId();

  const [categories, setCategories] = React.useState<
    { id: number; name: string }[]
  >([]);
  const [creatingCategory, setCreatingCategory] = React.useState(false);
  const [newCategory, setNewCategory] = React.useState('');

  React.useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    woocommerceApi
      .getCategories(businessId)
      .then((rows) => {
        if (cancelled) return;
        setCategories(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const handleFiles = async (files: FileList) => {
    const newItems: WooImageItem[] = Array.from(files).map((f) => ({
      url: URL.createObjectURL(f),
      uploading: true,
    }));
    onImagesChange((prev) => [...prev, ...newItems]);

    const uploaded: WooImageItem[] = await Promise.all(
      Array.from(files).map(async (file, i) => {
        try {
          const url = await woocommerceApi.uploadImage(businessId, file);
          return { url, uploading: false };
        } catch (e) {
          return {
            url: newItems[i].url,
            uploading: false,
            error: e instanceof Error ? e.message : 'Upload failed',
          };
        }
      }),
    );

    onImagesChange((prev) => {
      const kept = prev.filter((img) => !img.uploading);
      return [...kept, ...uploaded];
    });
  };

  const [dragging, setDragging] = React.useState(false);

  const primaryImageIndex = images.findIndex((img) => !img.error);

  const removeImage = (idx: number) =>
    onImagesChange((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className={PRODUCT_FORM_LABEL_CLASS}>Product Images</Label>
        <input
          id={fileInputId}
          type="file"
          accept="image/*"
          multiple
          className="peer sr-only"
          onChange={(e) => {
            if (e.target.files?.length) {
              handleFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />
        {images.length === 0 ? (
          <ImageDropZone
            htmlFor={fileInputId}
            dragging={dragging}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
            }}
          />
        ) : (
          <div
            className={cn(
              'rounded-xl border-2 border-dashed p-3 transition-colors',
              dragging ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200',
            )}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
            }}
          >
            <div className="grid grid-cols-5 gap-2">
              {images.map((img, i) => (
                <div
                  key={i}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm"
                >
                  <img
                    src={img.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {i === primaryImageIndex && (
                    <div className="absolute top-1.5 left-1.5">
                      <Badge className="text-[9px] px-1.5 py-0 h-4 bg-black/70 text-white border-0 hover:bg-black/70">
                        Primary
                      </Badge>
                    </div>
                  )}
                  {img.uploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 text-white animate-spin" />
                    </div>
                  )}
                  {img.error && (
                    <div className="absolute inset-0 bg-destructive/70 flex items-center justify-center p-1">
                      <span className="text-[9px] text-white text-center leading-tight">
                        Upload failed
                      </span>
                    </div>
                  )}
                  {!img.uploading && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(i);
                      }}
                      className="absolute top-1.5 right-1.5 w-5 h-5 bg-black/60 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
              <input
                id={`${fileInputId}-add`}
                type="file"
                accept="image/*"
                multiple
                className="peer sr-only"
                onChange={(e) => {
                  if (e.target.files?.length) {
                    handleFiles(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
              <label
                htmlFor={`${fileInputId}-add`}
                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-colors group peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500 peer-focus-visible:ring-offset-2"
              >
                <Plus className="h-5 w-5 text-slate-400 group-hover:text-emerald-500" />
                <span className="text-[10px] text-slate-400 group-hover:text-emerald-500 mt-1">
                  Add
                </span>
              </label>
            </div>
          </div>
        )}

        {onAddImageUrl && onImageUrlChange && (
          <div className="mt-2 flex gap-2">
            <Input
              className={PRODUCT_FORM_FIELD_CLASS}
              id={`${fileInputId}-image-url`}
              value={imageUrl ?? ''}
              onChange={(e) => onImageUrlChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onAddImageUrl();
                }
              }}
              placeholder="https://example.com/image.jpg"
            />
            <button
              type="button"
              onClick={onAddImageUrl}
              className={PRODUCT_FORM_INLINE_BUTTON_CLASS}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add URL
            </button>
          </div>
        )}
      </div>

      <div className={PRODUCT_FORM_ROW_CLASS}>
        <Label className={PRODUCT_FORM_LABEL_CLASS} htmlFor={`${fileInputId}-name`}>
          Name <span className={PRODUCT_FORM_REQUIRED_CLASS}>*</span>
        </Label>
        <Input
          className={PRODUCT_FORM_FIELD_CLASS}
          id={`${fileInputId}-name`}
          value={form.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="Product name"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className={PRODUCT_FORM_ROW_CLASS}>
          <Label className={PRODUCT_FORM_LABEL_CLASS} htmlFor={`${fileInputId}-regular-price`}>
            Regular Price ({currency})
          </Label>
          <Input
            className={PRODUCT_FORM_FIELD_CLASS}
            id={`${fileInputId}-regular-price`}
            type="number"
            step="0.01"
            value={form.regular_price}
            onChange={(e) => onChange('regular_price', e.target.value)}
          />
        </div>
        <div className={PRODUCT_FORM_ROW_CLASS}>
          <Label className={PRODUCT_FORM_LABEL_CLASS} htmlFor={`${fileInputId}-sale-price`}>
            Sale Price ({currency})
          </Label>
          <Input
            className={PRODUCT_FORM_FIELD_CLASS}
            id={`${fileInputId}-sale-price`}
            type="number"
            step="0.01"
            placeholder="Leave empty for no sale"
            value={form.sale_price}
            onChange={(e) => onChange('sale_price', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className={PRODUCT_FORM_ROW_CLASS}>
          <Label className={PRODUCT_FORM_LABEL_CLASS} htmlFor={`${fileInputId}-sku`}>SKU</Label>
          <Input
            className={PRODUCT_FORM_FIELD_CLASS}
            id={`${fileInputId}-sku`}
            value={form.sku}
            onChange={(e) => onChange('sku', e.target.value)}
          />
        </div>
        <div className={PRODUCT_FORM_ROW_CLASS}>
          <Label className={PRODUCT_FORM_LABEL_CLASS} htmlFor={`${fileInputId}-qty`}>Stock Quantity</Label>
          <Input
            className={PRODUCT_FORM_FIELD_CLASS}
            id={`${fileInputId}-qty`}
            type="number"
            value={form.stock_quantity}
            onChange={(e) => onChange('stock_quantity', e.target.value)}
          />
        </div>
      </div>

      <div className={PRODUCT_FORM_ROW_CLASS}>
        <Label className={PRODUCT_FORM_LABEL_CLASS}>Category</Label>
        <Select
          value={form.category ? form.category : '__none__'}
          onValueChange={(v) => {
            if (v === '__new__') {
              setNewCategory('');
              setCreatingCategory(true);
              return;
            }
            setCreatingCategory(false);
            onChange('category', v === '__none__' ? '' : v);
          }}
        >
          <SelectTrigger className={PRODUCT_FORM_FIELD_CLASS}>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {form.category &&
              !categories.some((c) => c.name === form.category) && (
                <SelectItem value={form.category}>{form.category}</SelectItem>
              )}
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.name}>
                {c.name}
              </SelectItem>
            ))}
            <SelectItem value="__new__">+ New category…</SelectItem>
          </SelectContent>
        </Select>

        {creatingCategory && (
          <div className="mt-2 flex items-center gap-2">
            <Input
              autoFocus
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!newCategory.trim()) return;
                  onChange('category', newCategory.trim());
                  setCreatingCategory(false);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setCreatingCategory(false);
                }
              }}
              placeholder="New category name"
              className={cn('flex-1', PRODUCT_FORM_FIELD_CLASS)}
            />
            <button
              type="button"
              onClick={() => {
                if (!newCategory.trim()) return;
                onChange('category', newCategory.trim());
                setCreatingCategory(false);
              }}
              disabled={!newCategory.trim()}
              className={cn(
                PRODUCT_FORM_INLINE_BUTTON_CLASS,
                'disabled:cursor-not-allowed disabled:opacity-60',
              )}
            >
              Use
            </button>
            <button
              type="button"
              onClick={() => setCreatingCategory(false)}
              className={PRODUCT_FORM_INLINE_BUTTON_CLASS}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className={PRODUCT_FORM_ROW_CLASS}>
          <Label className={PRODUCT_FORM_LABEL_CLASS}>Status</Label>
          <Select
            value={form.status}
            onValueChange={(v) => onChange('status', v)}
          >
            <SelectTrigger className={PRODUCT_FORM_FIELD_CLASS}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WOO_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={PRODUCT_FORM_ROW_CLASS}>
          <Label className={PRODUCT_FORM_LABEL_CLASS}>Stock Status</Label>
          <Select
            value={form.stock_status}
            onValueChange={(v) => onChange('stock_status', v)}
          >
            <SelectTrigger className={PRODUCT_FORM_FIELD_CLASS}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WOO_STOCK_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={PRODUCT_FORM_ROW_CLASS}>
        <Label className={PRODUCT_FORM_LABEL_CLASS} htmlFor={`${fileInputId}-short-desc`}>Short Description</Label>
        <Textarea
          className={PRODUCT_FORM_TEXTAREA_CLASS}
          id={`${fileInputId}-short-desc`}
          rows={3}
          value={form.short_description}
          onChange={(e) => onChange('short_description', e.target.value)}
        />
      </div>
    </div>
  );
}
